/**
 * Audio Metadata Extractor, SHA-1 Generator, and JS BPM Beat Detector
 * Handles OGG Vorbis comment parsing, ID3v2 tagging, Web Crypto SHA-1 hashing,
 * and Web Audio API peak energy autocorrelation for automatic BPM estimation.
 */

export interface ParsedAudioMetadata {
    title?: string;
    artist?: string;
    album?: string;
    bpm?: number;
    bpmSource?: 'metadata' | 'detected';
    durationMs?: number;
}

export const BPM_ANALYSIS_VERSION = 2;

// Aesthetic deterministic gradients
export const GRADIENTS = [
    'linear-gradient(135deg, #1db954 0%, #191414 100%)',
    'linear-gradient(135deg, #e91e63 0%, #3f51b5 100%)',
    'linear-gradient(135deg, #ff9800 0%, #e91e63 100%)',
    'linear-gradient(135deg, #9c27b0 0%, #2196f3 100%)',
    'linear-gradient(135deg, #00bcd4 0%, #3f51b5 100%)',
    'linear-gradient(135deg, #4caf50 0%, #009688 100%)',
    'linear-gradient(135deg, #ff5722 0%, #795548 100%)',
    'linear-gradient(135deg, #673ab7 0%, #e91e63 100%)',
    'linear-gradient(135deg, #3f51b5 0%, #00bcd4 100%)',
    'linear-gradient(135deg, #ff4081 0%, #7c4dff 100%)',
    'linear-gradient(135deg, #00e676 0%, #00b0ff 100%)',
    'linear-gradient(135deg, #ffab00 0%, #ff1744 100%)'
];

export function getDeterministicGradient(seed: string | number): string {
    let num = 0;
    if (typeof seed === 'number') {
        num = Math.abs(seed);
    } else {
        for (let i = 0; i < seed.length; i++) {
            num = (num << 5) - num + seed.charCodeAt(i);
            num |= 0;
        }
        num = Math.abs(num);
    }
    return GRADIENTS[num % GRADIENTS.length];
}

/**
 * Computes SHA-1 hash of a File using Web Crypto API.
 */
export async function calculateSHA1(file: File): Promise<string> {
    const buffer = await file.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest('SHA-1', buffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Formats a raw filename into a human-friendly dance track title.
 */
export function formatFilenameTitle(filename: string): string {
    const dotIndex = filename.lastIndexOf('.');
    const baseName = dotIndex > 0 ? filename.substring(0, dotIndex) : filename;
    let formatted = baseName
        .replace(/[_-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

    formatted = formatted.replace(/\bremastered\b/i, '(Remastered)');
    formatted = formatted.replace(/\bloop\b/i, '(Loop)');
    formatted = formatted.replace(/\bmix\b/i, '(Mix)');

    return formatted
        .split(' ')
        .map(w => {
            if (w.startsWith('(')) {
                return '(' + (w.slice(1, 2).toUpperCase() + w.slice(2));
            }
            return w.charAt(0).toUpperCase() + w.slice(1);
        })
        .join(' ');
}

/**
 * Parses OGG Vorbis comments (TITLE, ARTIST, ALBUM, BPM, TEMPO).
 */
export async function parseOggMetadata(file: File): Promise<ParsedAudioMetadata> {
    const headerSlice = file.slice(0, Math.min(file.size, 65536));
    const buffer = await headerSlice.arrayBuffer();
    const bytes = new Uint8Array(buffer);
    const decoder = new TextDecoder('utf-8');

    const result: ParsedAudioMetadata = {};

    // Look for "\x03vorbis" marker
    for (let i = 0; i < bytes.length - 7; i++) {
        if (
            bytes[i] === 0x03 &&
            bytes[i + 1] === 0x76 && // 'v'
            bytes[i + 2] === 0x6f && // 'o'
            bytes[i + 3] === 0x72 && // 'r'
            bytes[i + 4] === 0x62 && // 'b'
            bytes[i + 5] === 0x69 && // 'i'
            bytes[i + 6] === 0x73    // 's'
        ) {
            let offset = i + 7;
            const view = new DataView(buffer);

            // Vendor string length (32-bit uint little endian)
            if (offset + 4 > bytes.length) break;
            const vendorLen = view.getUint32(offset, true);
            offset += 4 + vendorLen;

            // User comment list length
            if (offset + 4 > bytes.length) break;
            const userCommentCount = view.getUint32(offset, true);
            offset += 4;

            for (let c = 0; c < userCommentCount && offset + 4 <= bytes.length; c++) {
                const commentLen = view.getUint32(offset, true);
                offset += 4;
                if (offset + commentLen > bytes.length) break;

                const commentBytes = bytes.slice(offset, offset + commentLen);
                offset += commentLen;

                const commentStr = decoder.decode(commentBytes);
                const eqIdx = commentStr.indexOf('=');
                if (eqIdx !== -1) {
                    const key = commentStr.substring(0, eqIdx).toUpperCase().trim();
                    const val = commentStr.substring(eqIdx + 1).trim();

                    if (key === 'TITLE') result.title = val;
                    else if (key === 'ARTIST') result.artist = val;
                    else if (key === 'ALBUM') result.album = val;
                    else if (key === 'BPM' || key === 'TEMPO') {
                        const parsedBpm = parseFloat(val);
                        if (!isNaN(parsedBpm) && parsedBpm > 40 && parsedBpm < 260) {
                            result.bpm = Math.round(parsedBpm);
                        }
                    }
                }
            }
            break;
        }
    }

    return result;
}

/**
 * Parses ID3v2 tags from MP3 files.
 */
export async function parseId3Metadata(file: File): Promise<ParsedAudioMetadata> {
    const slice = file.slice(0, Math.min(file.size, 32768));
    const buffer = await slice.arrayBuffer();
    const bytes = new Uint8Array(buffer);
    const result: ParsedAudioMetadata = {};

    if (bytes[0] === 0x49 && bytes[1] === 0x44 && bytes[2] === 0x33) { // 'ID3'
        const decoder = new TextDecoder('utf-8');
        const str = decoder.decode(bytes);

        // Simple tag finder for ID3v2
        const extractFrame = (frameId: string): string | undefined => {
            const idx = str.indexOf(frameId);
            if (idx === -1 || idx + 10 > str.length) return undefined;
            const sliceStr = str.substring(idx + 10, idx + 200);
            const nullIdx = sliceStr.indexOf('\0');
            const clean = (nullIdx !== -1 ? sliceStr.substring(0, nullIdx) : sliceStr).replace(/[^\x20-\x7E]/g, '').trim();
            return clean.length > 0 ? clean : undefined;
        };

        result.title = extractFrame('TIT2');
        result.artist = extractFrame('TPE1');
        result.album = extractFrame('TALB');
        const bpmStr = extractFrame('TBPM');
        if (bpmStr) {
            const b = parseFloat(bpmStr);
            if (!isNaN(b) && b > 40 && b < 260) result.bpm = Math.round(b);
        }
    }

    return result;
}

/**
 * Automatically computes BPM using Web Audio API buffer analysis.
 * Samples the audio signal, runs an autocorrelation peak detection algorithm in JavaScript.
 */
export async function detectBpmFromAudio(file: File): Promise<number | null> {
    try {
        const AudioContextClass = window.AudioContext || window.webkitAudioContext;
        if (!AudioContextClass) return null;

        // Decode a bounded file prefix, then analyze up to 45 seconds after the intro.
        const slice = file.slice(0, Math.min(file.size, 4 * 1024 * 1024));
        const arrayBuffer = await slice.arrayBuffer();

        const ctx = new AudioContextClass();
        const audioBuffer = await ctx.decodeAudioData(arrayBuffer);
        ctx.close();

        const channelData = audioBuffer.getChannelData(0);
        const sampleRate = audioBuffer.sampleRate;

        // Build a 100 Hz onset-strength envelope from short RMS frames. Positive
        // energy changes emphasize beat attacks instead of sustained loudness.
        const frameSize = Math.max(1, Math.round(sampleRate * 0.04));
        const hopSize = Math.max(1, Math.round(sampleRate * 0.01));
        const startSample = Math.min(channelData.length, Math.round(sampleRate * 5));
        const endSample = Math.min(channelData.length, startSample + Math.round(sampleRate * 45));
        const onsetEnvelope: number[] = [];
        let previousRms = 0;

        for (let offset = startSample; offset + frameSize < endSample; offset += hopSize) {
            let energy = 0;
            for (let i = 0; i < frameSize; i++) {
                const sample = channelData[offset + i];
                energy += sample * sample;
            }
            const rms = Math.sqrt(energy / frameSize);
            onsetEnvelope.push(Math.max(0, rms - previousRms));
            previousRms = rms;
        }

        if (onsetEnvelope.length < 2) return null;
        const mean = onsetEnvelope.reduce((total, value) => total + value, 0) / onsetEnvelope.length;
        for (let i = 0; i < onsetEnvelope.length; i++) onsetEnvelope[i] -= mean;

        const envelopeRate = sampleRate / hopSize;
        const minInterval = Math.floor((60 / 180) * envelopeRate);
        const maxInterval = Math.ceil((60 / 50) * envelopeRate);
        let bestInterval = 0;
        let bestCorrelation = 0;

        for (let interval = minInterval; interval <= maxInterval; interval++) {
            let correlation = 0;
            let leftEnergy = 0;
            let rightEnergy = 0;
            for (let i = 0; i + interval < onsetEnvelope.length; i++) {
                const left = onsetEnvelope[i];
                const right = onsetEnvelope[i + interval];
                correlation += left * right;
                leftEnergy += left * left;
                rightEnergy += right * right;
            }
            const normalized = correlation / Math.sqrt(leftEnergy * rightEnergy || 1);
            if (normalized > bestCorrelation) {
                bestCorrelation = normalized;
                bestInterval = interval;
            }
        }

        if (bestInterval > 0 && bestCorrelation >= 0.05) {
            return Math.round((60 * envelopeRate) / bestInterval);
        }
    } catch {
        // Fallback gracefully if Web Audio decoding is unsupported for slice
    }
    return null;
}

/**
 * Extracts complete metadata for a given audio file.
 */
export async function extractTrackMetadata(file: File, relativePath: string): Promise<ParsedAudioMetadata> {
    const ext = '.' + file.name.split('.').pop()?.toLowerCase();
    let meta: ParsedAudioMetadata = {};

    if (ext === '.ogg' || ext === '.oga') {
        meta = await parseOggMetadata(file);
    } else if (ext === '.mp3') {
        meta = await parseId3Metadata(file);
    }

    if (!meta.title) {
        meta.title = formatFilenameTitle(file.name);
    }
    if (!meta.artist) {
        meta.artist = 'Dance Sessions';
    }
    if (!meta.album) {
        const parts = relativePath.split('/');
        meta.album = parts.length > 1 ? parts[parts.length - 2] : 'Dance Vault';
    }

    // Measure duration using Audio element if possible
    try {
        const objectUrl = URL.createObjectURL(file);
        const tempAudio = new Audio();
        tempAudio.src = objectUrl;

        await new Promise<void>((resolve) => {
            const cleanup = () => {
                URL.revokeObjectURL(objectUrl);
                resolve();
            };
            tempAudio.onloadedmetadata = () => {
                if (tempAudio.duration && isFinite(tempAudio.duration)) {
                    meta.durationMs = Math.round(tempAudio.duration * 1000);
                }
                cleanup();
            };
            tempAudio.onerror = cleanup;
            setTimeout(cleanup, 2000); // 2s timeout
        });
    } catch {
        // Ignore duration measurement errors
    }

    // Detect BPM if not present in tags
    if (!meta.bpm) {
        const detected = await detectBpmFromAudio(file);
        if (detected !== null) {
            meta.bpm = detected;
            meta.bpmSource = 'detected';
        }
    } else {
        meta.bpmSource = 'metadata';
    }

    return meta;
}
